// packages/scene/src/board/focus-style.ts
//
// `focus` — figure/ground, folded onto a placed board.
//
// The design is in `docs/plans/2026-08-25-view-focus.md`. Two rules carry it:
//
//   1. THE GROUND IS OCCLUDED, NOT REPAINTED. A ground item keeps every pixel
//      of its own paint and is covered by a translucent veil in the board
//      canvas's own colour, drawn LAST in that item's group — above the
//      composed node, above the nested space, and outside anything that fades
//      the node. So hue (aspect / `style { color }` / diff), opacity
//      (lifecycle), dash, strike, badge and shape all survive underneath, and a
//      node's colour still traces to its legend row: it is behind glass, not
//      recoloured. It also reaches what no repaint could — a glyph figure is
//      raw SVG with baked hexes and has no chroma knob at all.
//
//   2. AN ITEM IS FIGURE IF IT, OR ANY DESCENDANT, IS FIGURE. So a ground
//      container is one whose entire subtree is ground, and veiling it whole is
//      correct; a figure container is never veiled and its ground children each
//      get their own veil. Recursive, no special case.
//
// This module only STAMPS the roles. The veil itself is drawn at the item layer
// by each backend (`render-svg` for export, `SpaceRenderer` live) — deliberately
// NOT as an `Overlay` inside the composed node, which would (a) be multiplied by
// the lifecycle opacity set on the node's root box, so already-ghosted nodes
// would recede LEAST, (b) turn clipping on for every composer whose first
// overlay this became, and (c) still not cover a nested space, which both
// backends paint above their own node.

import type { BoardEdge, BoardItem, BoardSpace, ExternalPort, FocusRole } from "./types.js";
import { logicalKeyOf } from "./types.js";

/**
 * Whether this item is named in a figure set — THE key rule, exported so the
 * live resolver and this fold cannot drift apart.
 *
 * Both keys, because the set holds whatever `nodeIdsByOrigin` mapped a focused
 * origin onto and that is a BOARD id: a real `#id` where a module has one, a
 * synthetic `::m::<stampedKey>` for a grouped id-less member, a `g<i>::` copy
 * id under an aspect overlay. Only the last of those carries a `logicalId`, so
 * matching `logicalId` alone would miss every grouped member and matching `id`
 * alone would pin an overlay replica to the wrong entity.
 *
 * NOT `boardItemKey`: that one strips a leading `::` (it keys the lifecycle
 * paint map, which is built from ORIGIN names), and stripping turns
 * `::m::Commerce::Payments` into a string the plan never produces — so nothing
 * matched, and a focused board veiled uniformly INCLUDING its figure.
 */
export function isFigureKey(item: BoardItem, figureKeys: ReadonlySet<string>): boolean {
  return figureKeys.has(logicalKeyOf(item)) || figureKeys.has(item.id);
}

/**
 * Alpha of the ground veil — how much of the canvas is laid over a receded
 * item. Tuned against the REAL canvas colour at the visual gate; the colour
 * itself is never a scene token (`tokens.bg` is one rung lighter than the live
 * canvas and a different hue from the export one), it is passed in by whoever
 * knows what they are painting on.
 */
export const FOCUS_VEIL = 0.55;

/**
 * How much of a ground line survives the veil — the multiplier its stroke
 * opacity takes.
 *
 * A 1.5px line always has the canvas behind it, so laying the veil over it and
 * scaling its alpha are the same pixels: occluding content of opacity `a` with
 * a veil of alpha `v` leaves `a × (1 − v)`. That is a UNIFORM factor, which is
 * exactly why the veil belongs outside the node — a veil INSIDE would have been
 * scaled BY `a` instead, so an already-faded node would have received a weaker
 * veil and receded least of all.
 */
export const FOCUS_GROUND_ALPHA = 1 - FOCUS_VEIL;

/**
 * Fold figure/ground onto a placed space.
 *
 * `figureKeys` holds BOARD ids — not the view plan's origin-qualified names.
 * Whoever calls this owns that mapping (the plan carries `nodeIdsByOrigin` for
 * exactly this, and one origin maps to MANY board ids, which is what makes a
 * `group by` fan-out work: a focused module is figure in every group it joins
 * rather than in one). Matching is {@link isFigureKey}, shared with live.
 *
 * Returns the space BY REFERENCE when nothing is focused, so an unfocused board
 * allocates nothing and its export stays byte-identical.
 */
export function applyFocus(
  space: BoardSpace,
  figureKeys: ReadonlySet<string> | null | undefined,
): BoardSpace {
  if (!figureKeys || figureKeys.size === 0) return space;
  return foldSpace(space, figureKeys);
}

/** True when this item or anything below it is named in the figure set. */
function subtreeHasFigure(item: BoardItem, figureKeys: ReadonlySet<string>): boolean {
  if (isFigureKey(item, figureKeys)) return true;
  return !!item.space?.items.some((c) => subtreeHasFigure(c, figureKeys));
}

/**
 * The stroke opacity a ground line paints at, given its resting one.
 *
 * Applied by the BACKENDS at paint time, never folded into
 * `EdgeWidgetStyle.opacity` — the fold must stay idempotent, and baking the
 * factor into the style would compound it on every re-fold (0.45 → 0.2 → 0.09)
 * while also destroying the resting value the figure needs.
 */
export function groundStrokeOpacity(resting: number | undefined): number {
  return (resting ?? 1) * FOCUS_GROUND_ALPHA;
}

// No port fold. A port nub IS outside its owning item's box (it is drawn on the
// enclosing space's wall), so it looks like it needs its own role — but the
// export renders no `externalPorts` at ALL, and the live `PortsLayer` resolves
// the owning item's role itself. A stamp with no reader is not coverage.

/**
 * Clear every stamp inside a subtree the fold is about to stop descending into,
 * by reference when there is nothing to clear.
 *
 * Rule 2 means a ground container's own veil covers its whole subtree, so its
 * descendants must carry NO role of their own — a stamped ground descendant
 * would draw a second veil under the first, and 0.55 over 0.55 leaves 20% of
 * the node. Not descending is enough on a fresh space; it is NOT enough on one
 * a previous fold already stamped (a view switch that flips a container
 * figure→ground), and that is the case an unconditional stamp is supposed to
 * cover. So the descent still happens when there is something stale to erase.
 */
function stripFocus(space: BoardSpace): BoardSpace {
  let changed = false;
  const items = space.items.map((it) => {
    const inner = it.space ? stripFocus(it.space) : it.space;
    if (inner === it.space && it.focus === undefined) return it;
    changed = true;
    const { focus: _dropped, ...rest } = it;
    return { ...rest, ...(inner ? { space: inner } : {}) } as BoardItem;
  });
  if (!changed) return space;
  return { ...space, items };
}

function foldSpace(space: BoardSpace, figureKeys: ReadonlySet<string>): BoardSpace {
  const roleOf = new Map<string, FocusRole>();

  const foldItem = (it: BoardItem): BoardItem => {
    const role: FocusRole = subtreeHasFigure(it, figureKeys) ? "figure" : "ground";
    roleOf.set(it.id, role);
    // Recurse into a figure container to split it; into a ground one only to
    // ERASE what a previous fold left (see `stripFocus`) — stamping a ground
    // subtree would buy nothing and cost a second veil per node.
    const inner = it.space
      ? role === "figure"
        ? foldSpace(it.space, figureKeys)
        : stripFocus(it.space)
      : it.space;
    if (inner === it.space && it.focus === role) return it;
    return {
      ...it,
      // UNCONDITIONAL, for the reason `applyLifecycle` stamps unconditionally:
      // a re-fold on a view switch must CLEAR the previous view's role, and a
      // conditional stamp can only ever add. The by-reference guard above
      // already makes an unchanged item free.
      focus: role,
      ...(inner ? { space: inner } : {}),
    };
  };

  const items = space.items.map(foldItem);

  const foldEdge = (e: BoardEdge): BoardEdge => {
    // Both endpoints, or it is ground: a bright wire between two receded boxes
    // reads as an error, not as emphasis.
    //
    // An endpoint this space does not place is FIGURE, matching the live
    // `isGroundEdge`: the wire outruns the items (a cross-space call, an edge
    // to something the view hides), and there is no ground here to recede it
    // against. Defaulting the other way would recede such a wire in the file
    // and leave it bright on screen.
    const isFigure = (id: string) => !roleOf.has(id) || roleOf.get(id) === "figure";
    const role: FocusRole = isFigure(e.from) && isFigure(e.to) ? "figure" : "ground";
    return e.focus === role ? e : { ...e, focus: role };
  };

  return {
    ...space,
    items,
    ...(space.edges ? { edges: space.edges.map(foldEdge) } : {}),
  };
}
